import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Issue, IssueSeverity, IssueStatus, Project } from '../../../types';
import {
  getProjectIssues, createIssueWithFields, updateIssue, deleteIssue,
} from '../../../services/issueService';
import PeoplePicker, { Person } from '../../../components/PeoplePicker';
import ModuleGuard from '../../../components/ModuleGuard';
import Badge from '../../../components/Badge';
import FilterPills from '../../../components/FilterPills';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton, BM_LABEL, BM_INPUT, BM_TEXTAREA, BM_TITLE_INPUT,
} from '../../../components/BoardModal';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

const SEV_LABEL: Record<number, string> = { 100000000: 'Low', 100000001: 'Medium', 100000002: 'High', 100000003: 'Critical' };
const SEV_BADGE: Record<number, { bg: string; fg: string }> = {
  100000000: { bg: '#F3F4F6', fg: '#5A6272' },
  100000001: { bg: '#FEF3C7', fg: '#92400E' },
  100000002: { bg: '#FEE2E2', fg: '#991B1B' },
  100000003: { bg: '#DC2626', fg: '#fff' },
};
const STAT_LABEL: Record<number, string> = { 100000000: 'New', 100000001: 'In Progress', 100000002: 'Resolved', 100000003: 'Closed', 100000004: 'Deferred' };
const STAT_BADGE: Record<number, { bg: string; fg: string }> = {
  100000000: { bg: '#D4EEF5', fg: '#0A5568' },
  100000001: { bg: '#FEF3C7', fg: '#92400E' },
  100000002: { bg: '#D1FAE5', fg: '#065F46' },
  100000003: { bg: '#F3F4F6', fg: '#5A6272' },
  100000004: { bg: '#F3F4F6', fg: '#B8BEC9' },
};

type StatusFilter = 'all' | 100000000 | 100000001 | 100000002 | 100000003 | 100000004;
const FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all',       label: 'All' },
  { value: 100000000,   label: 'New' },
  { value: 100000001,   label: 'In Progress' },
  { value: 100000002,   label: 'Resolved' },
  { value: 100000003,   label: 'Closed' },
  { value: 100000004,   label: 'Deferred' },
];

export default function IssuesTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_issues">
      <IssuesTabBody {...props} />
    </ModuleGuard>
  );
}

function IssuesTabBody({ pca, projectId }: Props) {
  const [items, setItems]       = useState<Issue[]>([]);
  const [filter, setFilter]     = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<Issue | null>(null);
  const [showNew, setShowNew]   = useState(false);
  const [view, setView]         = useState<'table' | 'aging'>('table');
  // Aging cell filter: "sev-ageBucket" where ageBucket is 0..3.
  const [agingFilter, setAgingFilter] = useState<string | null>(null);

  const reload = () => getProjectIssues(pca, projectId).then(setItems);
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  const filtered = useMemo(() => {
    let list = filter === 'all' ? items : items.filter(i => i.dagrun_pm_status === filter);
    if (agingFilter) {
      const [sev, bucket] = agingFilter.split('-').map(n => Number(n));
      list = list.filter(i => i.dagrun_pm_severity === sev && ageBucket(i.createdon) === bucket);
    }
    return list;
  }, [items, filter, agingFilter]);

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Issues</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{
            display: 'inline-flex', gap: 2, padding: 2,
            background: '#F5F6F8', borderRadius: 5,
          }}>
            <ViewToggleButton active={view === 'table'} label="Table" onClick={() => { setView('table'); setAgingFilter(null); }} />
            <ViewToggleButton active={view === 'aging'} label="Aging" onClick={() => setView('aging')} />
          </div>
          <button
            type="button"
            onClick={() => setShowNew(true)}
            style={{
              background: '#0E7490', color: '#fff', border: 'none',
              padding: '8px 16px', borderRadius: 6,
              fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600,
              cursor: 'pointer',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#0A5568')}
            onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
          >+ Create Issue</button>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <FilterPills value={filter} options={FILTER_OPTIONS} onChange={setFilter} />
      </div>

      {view === 'aging' && (
        <IssueAgingHeatmap items={items} cellFilter={agingFilter} onCellClick={k => setAgingFilter(agingFilter === k ? null : k)} />
      )}

      {filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>
          No issues match this filter.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-ui)' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
              <Th>Title</Th><Th>Severity</Th><Th>Status</Th><Th>Owner</Th><Th>Created</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(i => (
              <tr key={i.dagrun_pm_issueid}
                onClick={() => setSelected(i)}
                style={{ borderBottom: '0.5px solid #EAECF0', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <Td><span style={{ color: '#0F1117', fontSize: 14 }}>{i.dagrun_pm_name}</span></Td>
                <Td><Badge {...SEV_BADGE[i.dagrun_pm_severity]}>{SEV_LABEL[i.dagrun_pm_severity]}</Badge></Td>
                <Td><Badge {...STAT_BADGE[i.dagrun_pm_status]}>{STAT_LABEL[i.dagrun_pm_status]}</Badge></Td>
                <Td>{i.dagrun_pm_ownerid ? (<span style={{ fontSize: 13, color: '#5A6272' }}>{i.dagrun_pm_ownerid.slice(0, 8)}…</span>) : <Em>—</Em>}</Td>
                <Td><span style={{ fontSize: 12, color: '#9198A8', fontFamily: 'var(--font-mono)' }}>{i.createdon ? new Date(i.createdon).toLocaleDateString() : '—'}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && (
        <CreateIssueModal pca={pca} projectId={projectId} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); reload(); }} />
      )}

      {selected && (
        <IssueDrawer
          pca={pca}
          issue={selected}
          onClose={() => { setSelected(null); reload(); }}
          onDeleted={() => { setSelected(null); reload(); }}
        />
      )}
    </div>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{
    padding: '8px 12px', fontWeight: 500, fontSize: 12, color: '#5A6272',
    textTransform: 'uppercase', letterSpacing: '0.06em',
  }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>
);
const Em = ({ children }: { children: React.ReactNode }) => (
  <span style={{ color: '#B8BEC9', fontSize: 13 }}>{children}</span>
);

// Age bucket: 0 = 0–7 days, 1 = 8–14, 2 = 15–30, 3 = 30+. Returns -1 if no createdon.
function ageBucket(createdon?: string | null): number {
  if (!createdon) return -1;
  const then = new Date(createdon).getTime();
  if (isNaN(then)) return -1;
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 7)  return 0;
  if (days <= 14) return 1;
  if (days <= 30) return 2;
  return 3;
}

const AGE_LABELS = ['0–7 days', '8–14 days', '15–30 days', '30+ days'];

// ── Issue Aging Heatmap ───────────────────────────────────────────
// Rows: severity (Critical/High/Medium/Low — top to bottom). Columns: age
// buckets. Cell intensity = severity-rank × age-rank normalised to [0, 1],
// applied as an opacity multiplier on a red base color. Empty cells get a
// very muted tint. Click a cell to filter the issues table below.
function IssueAgingHeatmap({ items, cellFilter, onCellClick }: {
  items: Issue[];
  cellFilter: string | null;
  onCellClick: (key: string) => void;
}) {
  // Severity ordering: Critical(3) at top, Low(0) at bottom.
  const sevs = [IssueSeverity.Critical, IssueSeverity.High, IssueSeverity.Medium, IssueSeverity.Low];
  const sevRank: Record<number, number> = {
    [IssueSeverity.Low]: 1, [IssueSeverity.Medium]: 2, [IssueSeverity.High]: 3, [IssueSeverity.Critical]: 4,
  };

  const counts = new Map<string, number>();
  let noPriority = 0;
  items.forEach(i => {
    if (i.dagrun_pm_severity == null) { noPriority++; return; }
    const b = ageBucket(i.createdon);
    if (b < 0) return;
    const k = `${i.dagrun_pm_severity}-${b}`;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  });

  const intensity = (sev: number, bucket: number): number => {
    // severity * (bucket weight 0.25, 0.45, 0.7, 1.0)
    const bw = [0.25, 0.45, 0.7, 1.0][bucket] ?? 0;
    const sw = sevRank[sev] / 4;
    return Math.max(0.12, Math.min(0.9, sw * bw + 0.1));
  };

  return (
    <div style={{
      background: '#fff', border: '1px solid #EAECF0', borderRadius: 8,
      padding: 20, marginBottom: 20,
    }}>
      <div style={{ fontSize: 12, color: '#5A6272', marginBottom: 12 }}>
        Severity × Age. Click a cell to filter the table below.
        {cellFilter && (
          <button type="button" onClick={() => onCellClick(cellFilter)}
            style={{ marginLeft: 10, background: 'transparent', color: '#0E7490', border: '1px solid #0E7490', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
          >Clear filter</button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(4, 1fr)', gap: 4 }}>
        <div />
        {AGE_LABELS.map(a => (
          <div key={`h-${a}`} style={{
            textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#5A6272',
            textTransform: 'uppercase', letterSpacing: '0.06em', paddingBottom: 6,
          }}>{a}</div>
        ))}
        {sevs.map(sev => (
          <React.Fragment key={`r-${sev}`}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: '#5A6272',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              paddingRight: 10,
            }}>{SEV_LABEL[sev]}</div>
            {[0, 1, 2, 3].map(bucket => {
              const key = `${sev}-${bucket}`;
              const count = counts.get(key) ?? 0;
              const op = intensity(sev, bucket);
              const active = cellFilter === key;
              return (
                <button key={key} type="button" onClick={() => onCellClick(key)}
                  style={{
                    background: count > 0 ? `rgba(220, 38, 38, ${op})` : 'rgba(0,0,0,0.02)',
                    border: active ? '2px solid #0E7490' : '1px solid rgba(0,0,0,0.05)',
                    borderRadius: 6,
                    padding: '18px 8px',
                    minHeight: 64,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 20, fontWeight: 700,
                    color: count > 0 && op > 0.55 ? '#fff' : count > 0 ? '#0F1117' : '#C4CAD4',
                    cursor: 'pointer',
                    transition: 'transform 0.08s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.02)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                >{count > 0 ? count : '·'}</button>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      {noPriority > 0 && (
        <div style={{ fontSize: 12, color: '#9198A8', marginTop: 14, fontStyle: 'italic' }}>
          {noPriority} issue{noPriority === 1 ? '' : 's'} without severity — set severity to include them.
        </div>
      )}
    </div>
  );
}

function ViewToggleButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        border: 'none',
        background: active ? '#D4EEF5' : 'transparent',
        color: active ? '#0E7490' : '#5A6272',
        fontWeight: active ? 500 : 400,
        fontFamily: 'var(--font-ui)',
        fontSize: 12, padding: '5px 12px',
        borderRadius: 4, cursor: 'pointer',
      }}
    >{label}</button>
  );
}

// ── Create Issue Modal ───────────────────────────────────────────────
function CreateIssueModal({ pca, projectId, onClose, onCreated }: { pca: IPublicClientApplication; projectId: string; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<number>(IssueSeverity.Medium);
  const [owner, setOwner] = useState<Person | null>(null);
  const [clientVisible, setClientVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      await createIssueWithFields(pca, projectId, {
        name: name.trim(),
        description: description.trim() || null,
        severity,
        ownerid: owner?.aadObjectId ?? null,
        clientvisible: clientVisible,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BoardModal
      open
      title="Create Issue"
      onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim()}>{saving ? 'Creating…' : 'Create Issue'}</BMPrimaryButton>
      </>}
    >
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Title *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} style={BM_TITLE_INPUT} placeholder="Short, specific issue title" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Description</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} style={BM_TEXTAREA} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Severity</label>
          <select value={severity} onChange={e => setSeverity(Number(e.target.value))} style={BM_INPUT}>
            {[IssueSeverity.Low, IssueSeverity.Medium, IssueSeverity.High, IssueSeverity.Critical].map(v => (
              <option key={v} value={v}>{SEV_LABEL[v]}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={BM_LABEL}>Owner</label>
          <PeoplePicker pca={pca} value={owner} onChange={setOwner} placeholder="Assign owner…" />
        </div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5A6272', cursor: 'pointer' }}>
        <input type="checkbox" checked={clientVisible} onChange={e => setClientVisible(e.target.checked)} />
        Visible to clients
      </label>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

// ── Issue Detail Drawer ──────────────────────────────────────────────
function IssueDrawer({ pca, issue, onClose, onDeleted }: { pca: IPublicClientApplication; issue: Issue; onClose: () => void; onDeleted: () => void }) {
  const [draft, setDraft] = useState<Issue>(issue);
  const [ownerName, setOwnerName] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [openSection, setOpenSection] = useState({ details: true, description: true, resolution: true });
  const flashTimer = useRef<number | null>(null);
  const textDebounce = useRef<number | null>(null);

  useEffect(() => { setDraft(issue); setOwnerName(null); }, [issue]);

  const patch = async (fields: Partial<Issue>) => {
    setSaving(true);
    try {
      await updateIssue(pca, draft.dagrun_pm_issueid, fields as Record<string, unknown>);
      setSavedFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
    } finally { setSaving(false); }
  };

  const setAndPatch = <K extends keyof Issue>(k: K, v: Issue[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    patch({ [k]: v } as Partial<Issue>);
  };

  const setTextDebounced = <K extends keyof Issue>(k: K, v: Issue[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    textDebounce.current = window.setTimeout(() => patch({ [k]: v } as Partial<Issue>), 600);
  };

  const flushText = <K extends keyof Issue>(k: K, v: Issue[K]) => {
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    patch({ [k]: v } as Partial<Issue>);
  };

  const ownerPerson: Person | null = draft.dagrun_pm_ownerid
    ? { aadObjectId: draft.dagrun_pm_ownerid, displayName: ownerName ?? draft.dagrun_pm_ownerid }
    : null;

  const showResolution = draft.dagrun_pm_status === IssueStatus.Resolved || draft.dagrun_pm_status === IssueStatus.Closed;

  const doDelete = async () => {
    setSaving(true);
    try { await deleteIssue(pca, draft.dagrun_pm_issueid); onDeleted(); } finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 480,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9198A8', opacity: savedFlash ? 1 : 0, transition: 'opacity 0.2s' }}>
            {savedFlash ? 'Saved ✓' : ''}
          </span>
          <button onClick={onClose} aria-label="Close" style={{
            width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer',
            color: '#5A6272', fontSize: 18,
          }}>×</button>
        </div>

        <input
          value={draft.dagrun_pm_name}
          onChange={e => setTextDebounced('dagrun_pm_name', e.target.value)}
          onBlur={e => flushText('dagrun_pm_name', e.target.value)}
          style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', border: 'none', padding: 0, marginBottom: 8, width: '100%', background: 'transparent' }}
        />

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 16 }}>
          <select value={draft.dagrun_pm_severity}
            onChange={e => setAndPatch('dagrun_pm_severity', Number(e.target.value))}
            style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
            {[IssueSeverity.Low, IssueSeverity.Medium, IssueSeverity.High, IssueSeverity.Critical].map(v => (
              <option key={v} value={v}>{SEV_LABEL[v]}</option>
            ))}
          </select>
          <select value={draft.dagrun_pm_status}
            onChange={e => setAndPatch('dagrun_pm_status', Number(e.target.value))}
            style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
            {[IssueStatus.New, IssueStatus.InProgress, IssueStatus.Resolved, IssueStatus.Closed, IssueStatus.Deferred].map(v => (
              <option key={v} value={v}>{STAT_LABEL[v]}</option>
            ))}
          </select>
        </div>

        <Section title="Details" open={openSection.details} onToggle={() => setOpenSection(s => ({ ...s, details: !s.details }))}>
          <div style={{ marginBottom: 12 }}>
            <label style={BM_LABEL}>Owner</label>
            <PeoplePicker pca={pca} value={ownerPerson}
              onChange={p => { setDraft(d => ({ ...d, dagrun_pm_ownerid: p?.aadObjectId ?? null })); setOwnerName(p?.displayName ?? null); patch({ dagrun_pm_ownerid: p?.aadObjectId ?? null }); }}
              placeholder="Select owner…"
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5A6272', cursor: 'pointer', marginBottom: 12 }}>
            <input type="checkbox" checked={!!draft.dagrun_pm_clientvisible}
              onChange={e => setAndPatch('dagrun_pm_clientvisible', e.target.checked)} />
            Visible to clients
          </label>
          <div style={{ fontSize: 12, color: '#9198A8' }}>
            Created {issue.createdon ? new Date(issue.createdon).toLocaleString() : '—'}
          </div>
        </Section>

        <Section title="Description" open={openSection.description} onToggle={() => setOpenSection(s => ({ ...s, description: !s.description }))}>
          <textarea value={draft.dagrun_pm_description ?? ''}
            onChange={e => setTextDebounced('dagrun_pm_description', e.target.value)}
            onBlur={e => flushText('dagrun_pm_description', e.target.value)}
            style={BM_TEXTAREA}
          />
        </Section>

        {showResolution && (
          <Section title="Resolution Notes" open={openSection.resolution} onToggle={() => setOpenSection(s => ({ ...s, resolution: !s.resolution }))}>
            <textarea value={draft.dagrun_pm_resolutionnotes ?? ''}
              onChange={e => setTextDebounced('dagrun_pm_resolutionnotes', e.target.value)}
              onBlur={e => flushText('dagrun_pm_resolutionnotes', e.target.value)}
              style={BM_TEXTAREA}
            />
          </Section>
        )}

        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
          <BMDangerButton onClick={() => setConfirmDelete(true)}>Delete Issue</BMDangerButton>
        </div>
      </div>

      {confirmDelete && (
        <BoardModal
          open
          title="Delete Issue"
          onClose={() => setConfirmDelete(false)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(false)} disabled={saving} />
            <BMDangerButton onClick={doDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete'}</BMDangerButton>
          </>}
        >
          Delete <strong style={{ color: '#0F1117' }}>{draft.dagrun_pm_name}</strong>? This cannot be undone.
        </BoardModal>
      )}
    </>
  );
}

function Section({ title, open, onToggle, children }: { title: string; open: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div>
      <div onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 11, fontWeight: 600, color: '#5A6272',
        textTransform: 'uppercase', letterSpacing: '0.12em',
        cursor: 'pointer', userSelect: 'none',
        padding: '12px 0 0',
        borderTop: '0.5px solid #EAECF0',
        marginTop: 16,
      }}>
        <span>{title}</span>
        <span style={{ fontSize: 10, color: '#9198A8' }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && <div style={{ paddingTop: 12 }}>{children}</div>}
    </div>
  );
}
